import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MediaRail } from './media-rail';
import { Film, Play, RefreshCw, X } from 'lucide-react';
import type { Meta } from '@streamit/contracts';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { safeImage } from '@/lib/catalog';
import { useSession } from './session';
import './continue-watching.css';

type SavedPlayback = { meta: Meta; videoId: string; position: number; duration: number };
export type ContinueWatchingState = ReturnType<typeof useContinueWatching>;

/** Saved progress for the signed-in profile. Owned by a stable parent so the row can be
 * shown in different layout slots (after the hero, or alone) without refetching. */
export function useContinueWatching(enabled = true) {
  const { data, authorizationVersion } = useSession();
  const profileId = enabled ? data?.profileId : undefined, userId = data?.user.id;
  const scope = `${userId}:${profileId}:${authorizationVersion}`;
  // All fetched/removal state carries the scope that produced it. Values are
  // exposed only when that tag matches the scope being rendered, so the first
  // render for a new profile never shows the old profile's cards, error or
  // pending removal, even before effects run.
  type Owned = { scope: string; items: SavedPlayback[]; error: string; loading: boolean; removing: string };
  const [owned, setOwned] = useState<Owned>({ scope: '', items: [], error: '', loading: false, removing: '' });
  const [revision, refresh] = useState(0);
  const lastFetch = useRef(0);
  const activeFetch = useRef<{ scope: string; controller: AbortController } | null>(null);
  // The committed scope. Updated in a layout effect (before any passive effect or
  // later response), and cleared on unmount, so responses from a retired scope
  // are rejected from the moment the new scope is on screen.
  const live = useRef<string | null>(null);
  useLayoutEffect(() => { live.current = scope; return () => { live.current = null; }; }, [scope]);
  const update = (mine: string, change: (old: Owned) => Partial<Owned>) => {
    if (live.current !== mine) return;
    setOwned(old => (old.scope === mine ? { ...old, ...change(old) } : old));
  };
  useEffect(() => {
    const mine = scope;
    // Keep this scope's cards mounted during refresh so the rail retains its
    // scroll position. A pending removal still owns its busy/focus state.
    // A new scope starts empty; the render-time scope check also hides old rows
    // before this effect runs.
    setOwned(old => old.scope === mine
      ? { ...old, error: '', loading: !!profileId }
      : { scope: mine, items: [], error: '', loading: !!profileId, removing: '' });
    if (!profileId) return;
    const controller = new AbortController();
    const request = { scope: mine, controller };
    activeFetch.current = request;
    lastFetch.current = Date.now();
    api<SavedPlayback[]>(`/profiles/${profileId}/continue`, { signal: controller.signal })
      .then(rows => { if (!controller.signal.aborted) update(mine, () => ({ items: rows })); })
      .catch(e => { if (!controller.signal.aborted) update(mine, () => ({ error: (e as Error).message })); })
      .finally(() => {
        if (!controller.signal.aborted) update(mine, () => ({ loading: false }));
        if (activeFetch.current === request) activeFetch.current = null;
      });
    return () => {
      controller.abort();
      if (activeFetch.current === request) activeFetch.current = null;
    };
  }, [scope, profileId, revision]);
  useEffect(() => {
    if (!profileId) return;
    const resume = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastFetch.current > 30_000) refresh(n => n + 1);
    };
    document.addEventListener('visibilitychange', resume);
    return () => document.removeEventListener('visibilitychange', resume);
  }, [profileId]);
  async function remove(item: SavedPlayback) {
    if (!profileId || live.current !== scope) return;
    const mine = scope;
    update(mine, () => ({ removing: item.videoId, error: '' }));
    try {
      await api(`/profiles/${profileId}/history/${encodeURIComponent(item.videoId)}`, { method: 'DELETE' });
      // An in-flight refresh may contain progress captured before this DELETE.
      // Retire only the live scope's request, including abort-insensitive replies;
      // an old profile's removal must not cancel the new profile's fetch.
      const pending = activeFetch.current;
      const cancelledRefresh = live.current === mine && pending?.scope === mine;
      if (cancelledRefresh) {
        pending.controller.abort();
        activeFetch.current = null;
      }
      update(mine, old => ({
        items: old.items.filter(row => row.videoId !== item.videoId),
        loading: cancelledRefresh ? false : old.loading,
      }));
    } catch (e) { update(mine, () => ({ error: (e as Error).message })); }
    finally { update(mine, old => (old.removing === item.videoId ? { removing: '' } : {})); }
  }
  const current = owned.scope === scope;
  const items = current ? owned.items : [], error = current ? owned.error : '';
  const loading = current ? owned.loading : !!profileId, removing = current ? owned.removing : '';
  return { items, error, loading, removing, scope, refresh: () => refresh(n => n + 1), remove };
}

export function ContinueWatchingRow({ state, onResume }: { state: ContinueWatchingState; onResume: (meta: Meta, videoId: string) => void }) {
  const { items, error, loading, removing, refresh, remove } = state;
  const row = useRef<HTMLDivElement>(null), refocus = useRef<{ index: number; scope: string } | null>(null);
  // After removing a focused card, keep focus on the card that takes its place,
  // then the row's Refresh button, then the page's main region (a stable,
  // programmatic target) — but only if focus is still ours: inside this row or
  // lost to <body>. If the user moved elsewhere while the DELETE was pending,
  // leave their focus alone. A removal from a retired profile scope never refocuses.
  // Process the return at commit: a passive effect from the preceding render
  // could otherwise consume a newly clicked removal before its busy state commits.
  useLayoutEffect(() => {
    const pending = refocus.current;
    if (!pending || removing) return;
    refocus.current = null;
    if (pending.scope !== state.scope) return;
    const active = document.activeElement;
    const ours = !active || active === document.body || !document.contains(active) || !!row.current?.contains(active);
    if (!ours) return;
    const resumes = [...(row.current?.querySelectorAll<HTMLButtonElement>('.continue-resume') || [])];
    const target = resumes[Math.min(pending.index, resumes.length - 1)]
      || row.current?.closest('section')?.querySelector<HTMLButtonElement>('[aria-label="Refresh Continue Watching"]')
      || document.querySelector<HTMLElement>('#main-content');
    target?.focus({ preventScroll: !resumes.length });
  }, [items, removing, state.scope]);
  if (!items.length && !error && !loading) return null;
  return <section className="media-section continue-section" aria-label="Continue Watching" data-scope={import.meta.env.DEV ? state.scope : undefined}>
    <div className="section-title"><h2>Continue Watching</h2>
      <Button variant="ghost" disabled={loading} focusableWhenDisabled aria-busy={loading} onClick={refresh} aria-label="Refresh Continue Watching"><RefreshCw size={16} /></Button>
    </div>
    {loading && <p role="status" className="muted">Loading your progress…</p>}
    {error && <p role="alert" className="notice">{error}</p>}
    <div ref={row}><MediaRail label="Continue Watching" resetKey={state.scope} className="continue-row">{items.map((item, index) => <article className="continue-card" key={JSON.stringify([item.meta.type, item.meta.id, item.videoId])}>
      <button className="continue-resume" aria-label={`Resume ${item.meta.name}`} onClick={() => onResume(item.meta, item.videoId)}>
        <div className="continue-art">{safeImage(item.meta.background || item.meta.poster)
          ? <img src={safeImage(item.meta.background || item.meta.poster)} alt="" loading="lazy" decoding="async" /> : <Film size={36} />}
          <span className="continue-play"><Play size={24} fill="currentColor" /></span>
        </div>
        <strong>{item.meta.name}</strong>
        <small>{Math.floor(item.position / 60)}:{String(Math.floor(item.position % 60)).padStart(2, '0')} watched</small>
        {item.duration > 0 && <progress aria-label={`${item.meta.name} progress`} max={item.duration} value={Math.min(item.position, item.duration)} />}
      </button>
      <button className="continue-remove" aria-label={`Remove ${item.meta.name} from Continue Watching`} disabled={!!removing} onClick={() => { if ((document.activeElement as HTMLElement | null)?.closest('.continue-card')) refocus.current = { index, scope: state.scope }; void remove(item); }}><X size={15} /></button>
    </article>)}</MediaRail></div>
  </section>;
}

/** Self-contained variant for screens that don't need a stable owner (Library). */
export function ContinueWatching({ onResume }: { onResume: (meta: Meta, videoId: string) => void }) {
  return <ContinueWatchingRow state={useContinueWatching()} onResume={onResume} />;
}
